/* eslint-disable */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Lock, Pencil, Target } from "lucide-react";
import api from "@/lib/api";
import {
  Panel, StatusPill, EmptyState, Loading, SectionLabel, HEALTH_LABELS,
} from "@/components/app/primitives";
import { MetricKpiCard, Delta } from "@/components/app/mc";
import { MoMChart } from "@/components/app/MoMChart";
import { EditMetricDialog, TargetDialog, CacExpensesPanel } from "@/components/app/MetricsEditor";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import {
  moneyCompact, moneyFull, pct, months as monthsFmt, ratioLabel, monthShort, EMPTY_MARK,
} from "@/lib/format";

const RATIO_TONE = { on_track: "on_track", at_risk: "at_risk", blocked: "blocked", unknown: "neutral" };
const RATIO_TEXT = { on_track: "Healthy", at_risk: "Watch", blocked: "Below 1:1", unknown: "No data" };

export default function VentureCommandCenter() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/mission-control/ventures/${id}`);
      setData(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load the command center.");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const months = useMemo(() => (data && !data.restricted ? data.months || [] : []), [data]);
  const latest = data && !data.restricted ? data.latest : null;
  const prev = months.length >= 2 ? months[months.length - 2] : null;
  const comp = latest?.computed || {};

  const chartData = useMemo(() => months.map((m) => ({
    label: monthShort(m.month),
    cash: m.cash_in_bank ?? null,
    revenue: m.revenue ?? null,
    burn: m.monthly_burn ?? null,
    runway: m.computed?.runway_months ?? null,
  })), [months]);

  const rawDelta = (field) => {
    if (!prev || !latest) return undefined;
    const a = latest[field]; const b = prev[field];
    if (a == null || b == null) return undefined;
    return Number(a) - Number(b);
  };
  const compDelta = (field) => {
    if (!prev || !latest) return undefined;
    const a = latest.computed?.[field]; const b = prev.computed?.[field];
    if (a == null || b == null) return undefined;
    return Number(a) - Number(b);
  };

  if (loading) return <Loading label="Loading command center" />;
  if (!data) return <EmptyState message="Nothing to show yet." />;

  const venture = data.venture;

  const BackBar = (
    <button data-testid="command-center-back" onClick={() => navigate("/mission-control")} className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
      <ArrowLeft className="h-3.5 w-3.5" strokeWidth={1.75} /> Mission Control
    </button>
  );

  if (data.restricted) {
    return (
      <div className="space-y-5">
        {BackBar}
        <Panel title={`${venture.name} command center`} bodyClassName="p-6">
          <div data-testid="command-center-restricted" className="flex flex-col items-center justify-center gap-2 py-10 text-center">
            <Lock className="h-6 w-6 text-muted-foreground" strokeWidth={1.5} />
            <p className="text-sm text-foreground">Venture metrics are restricted to the studio team.</p>
            <p className="text-xs text-muted-foreground">You can still see the organisation and structure on Mission Control.</p>
          </div>
        </Panel>
      </div>
    );
  }

  const target = data.ltv_cac_target || 3;
  const ratio = comp.ltv_cac_ratio;
  const ratioPctOfTarget = ratio != null ? Math.min((ratio / target) * 100, 100) : 0;
  const ratioStatus = comp.ltv_cac_status || "unknown";

  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-4">
        <div>
          {BackBar}
          <div className="mt-2 flex items-center gap-3">
            <h2 className="font-display text-2xl text-foreground leading-none">{venture.name}</h2>
            <StatusPill tone={venture.health} label={HEALTH_LABELS[venture.health] || venture.health} />
          </div>
          <p className="mt-1.5 text-xs text-muted-foreground">{venture.arm} <span className="text-muted-foreground/50">/</span> {venture.stage} <span className="text-muted-foreground/50">/</span> Month on month, manual entry</p>
        </div>
        {data.can_edit && (
          <EditMetricDialog
            ventureId={id}
            monthsData={months}
            defaultMonth={latest?.month}
            onSaved={load}
            trigger={(
              <Button data-testid="venture-command-center-edit-metrics" className="h-9 gap-2 bg-primary hover:bg-[#2746c0]">
                <Pencil className="h-4 w-4" strokeWidth={1.75} /> Edit metrics
              </Button>
            )}
          />
        )}
      </div>

      {months.length === 0 ? (
        <Panel bodyClassName="p-6">
          <EmptyState message="No month values recorded yet." />
          {data.can_edit && <p className="text-center text-xs text-muted-foreground">Use Edit metrics to add this month.</p>}
        </Panel>
      ) : (
        <>
          {/* KPI strip */}
          <div className="grid grid-cols-2 md:grid-cols-4 xl:grid-cols-8 gap-3">
            <MetricKpiCard testId="venture-kpi-cash" label="Cash in bank" value={moneyCompact(latest.cash_in_bank)}
              delta={<Delta value={rawDelta("cash_in_bank")} format={(v) => moneyCompact(v)} />} />
            <MetricKpiCard testId="venture-kpi-burn" label="Monthly burn" value={moneyCompact(latest.monthly_burn)}
              delta={<Delta value={rawDelta("monthly_burn")} invert format={(v) => moneyCompact(v)} />} />
            <MetricKpiCard testId="venture-kpi-runway" label="Runway" value={monthsFmt(comp.runway_months)}
              delta={<Delta value={compDelta("runway_months")} suffix=" mo" format={(v) => v.toFixed(1)} />} />
            <MetricKpiCard testId="venture-kpi-revenue" label="Revenue" value={moneyCompact(latest.revenue)}
              delta={<Delta value={rawDelta("revenue")} format={(v) => moneyCompact(v)} />} />
            <MetricKpiCard testId="venture-kpi-cac" label="CAC" value={comp.cac != null ? moneyFull(comp.cac) : EMPTY_MARK} sub={`${monthsFmt ? "" : ""}${comp.cac_total != null ? moneyCompact(comp.cac_total) + " spend" : ""}`}
              delta={<Delta value={compDelta("cac")} invert format={(v) => moneyFull(v)} />} />
            <MetricKpiCard testId="venture-kpi-ltv" label="LTV in use" value={comp.ltv_used != null ? moneyFull(comp.ltv_used) : EMPTY_MARK} sub={comp.ltv_is_manual ? "manual" : "derived"} />
            <MetricKpiCard testId="venture-kpi-ltv-derived" label="LTV derived" value={comp.ltv_derived != null ? moneyFull(comp.ltv_derived) : EMPTY_MARK} sub="ARPU / churn" />
            <MetricKpiCard testId="venture-kpi-churn" label="Churn" value={pct(latest.churn_rate)}
              delta={<Delta value={rawDelta("churn_rate")} invert suffix="%" format={(v) => v.toFixed(1)} />} />
          </div>

          {/* Trends + ratio */}
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Panel title="Cash, month on month" bodyClassName="p-3">
                <MoMChart data={chartData} dataKey="cash" formatter={(v) => moneyCompact(v)} testId="venture-mom-chart-cash" />
              </Panel>
              <Panel title="Revenue, month on month" bodyClassName="p-3">
                <MoMChart data={chartData} dataKey="revenue" formatter={(v) => moneyCompact(v)} testId="venture-mom-chart-revenue" />
              </Panel>
              <Panel title="Burn, month on month" bodyClassName="p-3">
                <MoMChart data={chartData} dataKey="burn" formatter={(v) => moneyCompact(v)} testId="venture-mom-chart-burn" />
              </Panel>
              <Panel title="Runway, month on month" bodyClassName="p-3">
                <MoMChart data={chartData} dataKey="runway" formatter={(v) => `${Number(v).toFixed(0)}`} testId="venture-mom-chart-runway" />
              </Panel>
            </div>

            <div className="col-span-12 lg:col-span-4">
              <Panel
                title="LTV to CAC"
                bodyClassName="p-4"
                action={data.can_edit && (
                  <TargetDialog
                    ventureId={id}
                    target={target}
                    onSaved={load}
                    trigger={(
                      <button data-testid="venture-cac-ltv-edit-target" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors">
                        <Target className="h-3.5 w-3.5" strokeWidth={1.75} /> Target
                      </button>
                    )}
                  />
                )}
              >
                <div className="flex items-end justify-between gap-3">
                  <span data-testid="venture-cac-ltv-ratio" className="font-mono tabular-nums text-3xl tracking-tight text-foreground">{ratioLabel(ratio)}</span>
                  <StatusPill tone={RATIO_TONE[ratioStatus]} label={RATIO_TEXT[ratioStatus]} />
                </div>
                <p data-testid="venture-cac-ltv-benchmark" className="mt-2 text-xs text-muted-foreground">
                  Ideal is at or above <span className="font-mono text-foreground">{ratioLabel(target)}</span>. A ratio below 1:1 means each customer costs more than they return.
                </p>
                <Progress value={ratioPctOfTarget} className="mt-3 h-2 bg-white/10" />
                <div className="mt-4 grid grid-cols-2 gap-3 border-t border-border pt-3">
                  <div><SectionLabel>CAC</SectionLabel><p className="mt-0.5 font-mono tabular-nums text-sm text-foreground">{comp.cac != null ? moneyFull(comp.cac) : EMPTY_MARK}</p></div>
                  <div><SectionLabel>ARPU</SectionLabel><p className="mt-0.5 font-mono tabular-nums text-sm text-foreground">{comp.arpu != null ? moneyFull(comp.arpu) : EMPTY_MARK}</p></div>
                </div>
              </Panel>
            </div>
          </div>

          {/* CAC expenses */}
          <Panel title="Customer Acquisition Cost expenses" testId="venture-cac-section" bodyClassName="p-4">
            <CacExpensesPanel
              ventureId={id}
              expenses={data.cac_expenses}
              categories={data.cac_categories}
              guidance={data.cac_guidance}
              canEdit={data.can_edit}
              defaultMonth={latest?.month}
              onChanged={load}
            />
          </Panel>
        </>
      )}
    </div>
  );
}
